import { FC, useState, useCallback, useContext, useRef } from "react";
import {
  styled,
  Typography,
  Stack,
  Chip,
  IconButton,
  Checkbox,
  Card,
  CardActionArea,
  CircularProgress,
  Link,
  Slide,
  Fade,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router";
import { Link as RouterLink } from "react-router";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import BookmarkOutlinedIcon from "@mui/icons-material/BookmarkOutlined";
import useCountMaxTags from "../../../Utils/Hooks/useCountMaxTags";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CheckboxBg from "../../Misc/CheckboxBg";
import { assetService } from "../../../Core/Api/services";
import { GetTitleRouterPath, UserHasAcl, UserHasLicence } from "../../../Utils/helpers";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import useBasketActions from "../../../Utils/Hooks/useBasketActions";
import { ModalContext } from "../../Modals";
import ModalCreateShare from "../../Modals/ModalCreateShare/modal-create-share";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { hexToRgbString } from "../../../Utils/Helpers/cssUtils";
import { Licences } from "../../../Core/Api/Types/licences";
import {
  getBroacasterIdMetadata,
  getBroadcasterImageSrc,
  getBundlesContent,
} from "../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { produce } from "immer";
import { useSelector } from "react-redux";
import { GetBroadcasterList } from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { CardBody, CardExpand, CardTop, CardTopLeft, MovieCardProps, StyledLabel, StyledLink } from "./movie-card";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useTranslation } from "react-i18next";

const MovieCard: FC<MovieCardProps> = (props) => {
  const { t } = useTranslation();
  const collectionActions = useCollectionActions();
  const navigate = useNavigate();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const broadcasters = useSelector(GetBroadcasterList);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetched, setFetched] = useState(false);

  const [lab, setLab] = useState<string>("");
  const [rightHolder, setRightHolder] = useState<string>("");
  const [platformLogos, setPlatformLogos] = useState<string[]>([]);
  const [hover, setHover] = useState(false);

  const {
    id,
    obj,
    selected,
    onSelectClick,
    expand,
    selectedOnClick,
    shareBtn,
    orderBtn,
    collectionBtn,
    selectable = true,
  } = props;
  const { visibleTags, hiddenTagsCount } = useCountMaxTags({
    tagList: expand.tagList,
  });

  // Hooks
  const { addObjectByIdsToBasket } = useBasketActions();
  const theme = useTheme();
  const movieCardRef = useRef<HTMLDivElement>(null);

  const handleCheckboxClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.stopPropagation();

      if (!onSelectClick || !obj) return;

      onSelectClick(obj);
    },
    [onSelectClick, obj]
  );

  const handleCardOver = useCallback(() => {
    if (isFetched) return;

    setIsLoading(true);

    assetService.GetGraph(id, true, true, true).then(function (response) {
      if (response && response.graph && response.graph.metadata) {
        setLab(response.graph.metadata.find((m) => m.fieldName === "vodLab")?.value || "N/A");
        setRightHolder(response.graph.metadata.find((m) => m.fieldName === "vodRightHolder")?.value || "N/A");

        const bundle = getBundlesContent(response.graph);
        const packageList =
          bundle?.childObjects
            ?.filter((x) => x.classification === ObjectClass.Project && !x.isDeleted)
            .sort((a, b) => b.id - a.id) || [];

        let packageMap: any = {};

        for (let i = 0; i < packageList.length; i++) {
          const broadcasterId = getBroacasterIdMetadata(packageList[i]);
          if (broadcasterId) {
            if (packageMap[broadcasterId] != null && packageMap[broadcasterId] != undefined) {
              packageMap[broadcasterId].push(packageList[i]);
            } else {
              packageMap[broadcasterId] = [packageList[i]];
            }
          }
        }
        setPlatformLogos([]);

        Object.keys(packageMap).map((x, i) => {
          setPlatformLogos(
            produce((draft) => {
              draft.push(getBroadcasterImageSrc(getBroacasterIdMetadata(packageMap[x][0]), broadcasters));
            })
          );
        });
        setIsLoading(false);
        setFetched(true);
      }
    });
  }, [isFetched, id]);

  const handleCardClick: React.MouseEventHandler = useCallback(
    (e) => {
      if ((selectedOnClick || e.ctrlKey) && onSelectClick && obj) {
        onSelectClick(obj);
      } else {
        navigate(GetTitleRouterPath() + "/" + id + "/bundlemaker");

        if (onSelectClick && obj) onSelectClick(obj);
      }
    },
    [onSelectClick, navigate, selectedOnClick, obj]
  );

  const handleAddToCollectionModal: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      collectionActions.AddItemsToCollection([new DbAssetObjectInfos({ id: id, name: props.title })]);
    },
    [id]
  );

  const handleShare: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();

      if (!openModal) return;

      assetService.GetObjectLightById(id, 0).then((response) => {
        openModal(ModalCreateShare, { object: response.graph });
      });
    },
    [id]
  );

  const handleOrder: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      addObjectByIdsToBasket([id]);
    },
    [id]
  );

  const handleCollection: React.MouseEventHandler = useCallback(
    (e) => {
      e.stopPropagation();
      collectionActions.AddItemByIdToCollection(id);
    },
    [id]
  );

  return (
    <StyledLink
      className="MuiMovieCard"
      selected={selected}
      hover={hover}
      onMouseEnter={(e) => !props.noHover && setHover(true)}
      onMouseLeave={(e) => !props.noHover && setHover(false)}
      ref={movieCardRef}
    >
      <CardActionArea onClick={handleCardClick} sx={{ flexDirection: "column", justifyContent: "space-between" }}>
        <Fade
          in={hover || selected}
          timeout={theme.transitions.duration.standard}
          easing={theme.transitions.easing.easeOut}
        >
          <CardTop>
            {props.selectMode ? (
              <>
                {selectable && (
                  <>
                    <CheckboxBg
                      checked={props.selected || false}
                      className="MuiCheckbox-Bg"
                      sx={{ top: "17px", left: "17px" }}
                    />
                    <Checkbox checked={props.selected || false} onClick={handleCheckboxClick}></Checkbox>
                  </>
                )}
              </>
            ) : (
              <>
                {selectable && (
                  <IconButton
                    sx={{
                      ml: 1,
                      mt: 1,
                      background: theme.palette.background.paper,
                      "&:hover": { background: theme.palette.background[200] },
                    }}
                    aria-label="favorite"
                    component="label"
                    onClick={handleAddToCollectionModal}
                    color="neutral"
                    size="small"
                  >
                    {props.favorite ? (
                      <BookmarkOutlinedIcon fontSize="small" className="action-icon" />
                    ) : (
                      <BookmarkBorderOutlinedIcon fontSize="small" className="action-icon" />
                    )}
                  </IconButton>
                )}
              </>
            )}
            <Stack gap={0.5} direction="row" p={theme.spacing(1)}>
              {shareBtn &&
                shareBtn === true &&
                UserHasAcl(AclTypes.aclShareCreate) &&
                UserHasLicence(Licences.ShareScreening) && (
                  <IconButton
                    aria-label="share"
                    component="label"
                    onClick={handleShare}
                    size="small"
                    color="neutral"
                    className="action-icon"
                    sx={{
                      background: theme.palette.background.paper,
                      "&:hover": { background: theme.palette.background[200] },
                    }}
                  >
                    <ShareOutlinedIcon fontSize="small" className="action-icon" />
                  </IconButton>
                )}
              {orderBtn && orderBtn === true && UserHasAcl(AclTypes.aclSectionOrderAccess) && (
                <IconButton
                  aria-label="order"
                  component="label"
                  onClick={handleOrder}
                  size="small"
                  color="neutral"
                  className="action-icon"
                  sx={{
                    background: theme.palette.background.paper,
                    "&:hover": { background: theme.palette.background[200] },
                  }}
                >
                  <ShoppingBagOutlinedIcon fontSize="small" className="action-icon" />
                </IconButton>
              )}
              {collectionBtn && collectionBtn === true && (
                <IconButton
                  aria-label="add to collection"
                  component="label"
                  onClick={handleCollection}
                  size="small"
                  color="neutral"
                  className="action-icon"
                  sx={{
                    background: theme.palette.background.paper,
                    "&:hover": { background: theme.palette.background[200] },
                  }}
                >
                  <BookmarkBorderIcon fontSize="small" className="action-icon" />
                </IconButton>
              )}
            </Stack>
          </CardTop>
        </Fade>
        {(props.noHover === undefined || props.noHover === false) && (
          <Slide
            in={hover}
            direction="up"
            timeout={theme.transitions.duration.standard}
            easing={theme.transitions.easing.easeOut}
            container={movieCardRef.current}
          >
            <CardExpand className="movie-card-footer">
              <Link
                underline="hover"
                color="text.primary"
                component={RouterLink}
                fontWeight={600}
                to={`${GetTitleRouterPath()}/${id}`}
                sx={{ pointerEvents: "auto", cursor: "pointer !important" }}
              >
                <Typography component="h3" variant="subtitle2" fontWeight={600}>
                  {props.title}
                </Typography>
              </Link>
              <Stack direction="row" flexWrap="wrap" spacing={0.5}>
                {visibleTags.map((tag) => (
                  <Chip color="primary" size="small" variant="outlined" label={tag} key={`${id}-${tag}`} />
                ))}
                {hiddenTagsCount > 0 && (
                  <Chip
                    color="primary"
                    size="small"
                    variant="outlined"
                    label={`+${hiddenTagsCount}`}
                    key={`${id}-tagmore`}
                  />
                )}
              </Stack>

              {isLoading ? (
                <Stack gap={0.5}>
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                  <Skeleton width={100} height={20} />
                </Stack>
              ) : (
                <Stack>
                  <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    {platformLogos.map((logo) =>
                      logo !== "" ? <img src={logo} alt="" width={16} height={16} loading="lazy" /> : <InventoryIcon />
                    )}
                  </Stack>

                  <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography component="span" variant="body2" fontWeight={500} key={id}>
                      {t("common:fromLowerCase")}:
                    </Typography>
                    <Chip label={rightHolder} component="span" variant="outlined" size="micro" />
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography component="span" variant="body2" fontWeight={500} key={id}>
                      {t("common:lowerCaseLab")}:
                    </Typography>
                    <Chip label={lab} component="span" variant="outlined" size="micro" />
                  </Stack>
                </Stack>
              )}
            </CardExpand>
          </Slide>
        )}
        <CardBody
          onMouseOver={(e) => {
            if (props.noHover) return;

            e.stopPropagation();
            e.preventDefault();
            handleCardOver();
          }}
          hover={hover}
        >
          <figure style={{ position: "relative", flexGrow: 1 }}>
            {props.cover && <img src={props.cover} alt={props.title} loading="lazy" />}
            {props.transparentChipText && (
              <Chip
                size="small"
                variant="filled"
                color="default"
                label={props.transparentChipText}
                sx={(theme) => ({
                  position: "absolute",
                  bottom: theme.spacing(1),
                  left: theme.spacing(1),
                  // backdropFilter: "blur(12px)",
                  // mixBlendMode: "difference",
                })}
              />
            )}
            <Fade in={!(hover || selected)}>
              <Stack spacing={0.5} sx={{ position: "absolute", top: 8, right: 0 }}>
                {props.labelList.map((label, index) => (
                  <StyledLabel
                    type={label === "NEW" ? "premium" : label === "DELETED" ? "top" : "new"}
                    key={`${id}-label-${index}`}
                    label={
                      label === "premium" ? (
                        <AutoAwesomeOutlinedIcon sx={{ fontSize: 14, position: "relative", top: 2 }} />
                      ) : (
                        label
                      )
                    }
                    size="small"
                  />
                ))}
              </Stack>
            </Fade>
          </figure>
          <Typography
            component="h3"
            variant="subtitle2"
            fontWeight={600}
            sx={(theme) => ({ ...theme.helpers(theme).clampedText })}
          >
            {props.title}
          </Typography>
        </CardBody>
      </CardActionArea>
    </StyledLink>
  );
};

export default MovieCard;
